import { useState } from 'preact/hooks';
import { activeContactSignal } from '../../state/contacts.store';
import { smartRepliesSignal, isGeneratingSmartRepliesSignal } from '../../state/ai.store';
import { apiFetch } from '../../services/api';
import { showToast } from '../../state/ui.store';
import { appendMessage } from '../../state/messages.store';
import type { Message } from '../../types/message';
import { Sparkles, Send, Bot, X } from 'lucide-preact';

export function SmartRepliesStream() {
  const contact = activeContactSignal.value;
  const replies = smartRepliesSignal.value;
  const isGenerating = isGeneratingSmartRepliesSignal.value;
  const [customDraft, setCustomDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  if (!contact) return null;

  const handleGenerateReplies = async () => {
    isGeneratingSmartRepliesSignal.value = true;
    try {
      const data = await apiFetch<{ replies: any[] }>(`/ai/smart-replies/${contact.phone_number}`, {
        method: 'POST'
      });
      if (data && data.replies) {
        smartRepliesSignal.value = data.replies;
      }
    } catch (err: any) {
      showToast(`Erro ao gerar respostas inteligentes: ${err.message}`, 'error');
    } finally {
      isGeneratingSmartRepliesSignal.value = false;
    }
  };

  const handleSendReply = async (text: string) => {
    if (!text.trim()) return;
    try {
      const res = await apiFetch<{ message: Message }>(`/messages/send/${contact.phone_number}`, {
        method: 'POST',
        body: JSON.stringify({ text: text.trim(), message: text.trim() })
      });
      if (res && res.message) {
        appendMessage(res.message);
      }
      showToast('Resposta enviada com sucesso!', 'success');
      smartRepliesSignal.value = [];
    } catch (err: any) {
      showToast(`Erro ao enviar resposta: ${err.message}`, 'error');
    }
  };

  if (isGenerating) {
    return (
      <div class="p-4 bg-cyan-950/40 border border-cyan-500/30 rounded-2xl animate-pulse text-xs text-cyan-300 flex items-center justify-center gap-2">
        <Bot class="w-4 h-4 text-cyan-400" />
        <span>Gerando sugestões de resposta contextualizadas...</span>
      </div>
    );
  }

  if (replies.length === 0) {
    return (
      <div class="smart-replies-trigger-card">
        <div class="flex items-center gap-2 text-xs text-slate-300">
          <Sparkles class="w-4 h-4 text-amber-300" />
          <span class="font-bold text-cyan-300">Respostas Inteligentes</span>
          <span class="text-slate-400 hidden sm:inline">— Obter sugestões de resposta para a última mensagem</span>
        </div>
        <button
          onClick={handleGenerateReplies}
          class="px-3.5 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold transition shadow-md shadow-cyan-600/20 cursor-pointer flex items-center gap-1.5"
        >
          <Sparkles class="w-3.5 h-3.5" />
          <span>Gerar Sugestões</span>
        </button>
      </div>
    );
  }

  const normalizedReplies = replies.map((r: any, idx: number) => {
    if (typeof r === 'string') {
      const titles = ['Opção 1 (Direta)', 'Opção 2 (Detalhada)', 'Opção 3 (Follow-up)'];
      return {
        id: String(idx + 1),
        title: titles[idx] || `Opção ${idx + 1}`,
        text: r,
        tone: `Opção ${idx + 1}`
      };
    }
    return {
      id: String(r.id || idx + 1),
      title: r.title || r.tone || `Opção ${idx + 1}`,
      text: r.text || r.content || (typeof r === 'object' ? JSON.stringify(r) : String(r)),
      tone: r.tone || 'Sugestão'
    };
  });

  return (
    <div class="space-y-3 p-4 bg-slate-950/80 border border-cyan-500/30 rounded-2xl animate-fade-in">
      <div class="flex items-center justify-between border-b border-slate-800 pb-2">
        <span class="text-xs font-bold text-cyan-300 flex items-center gap-1.5">
          <Sparkles class="w-4 h-4 text-amber-300" />
          <span>Sugestões de Resposta Inteligente</span>
        </span>
        <button
          onClick={() => smartRepliesSignal.value = []}
          class="text-xs text-slate-400 hover:text-slate-200 cursor-pointer flex items-center gap-1"
        >
          <span>Dispensar</span>
          <X class="w-3.5 h-3.5" />
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        {normalizedReplies.map(reply => (
          <div key={reply.id} class="smart-reply-draft-bubble p-3 flex flex-col justify-between space-y-2">
            <div>
              <span class="text-[10px] font-bold uppercase tracking-wider text-cyan-400 block mb-1">
                {reply.title}
              </span>
              {editingId === reply.id ? (
                <textarea
                  value={reply.text}
                  onInput={(e) => {
                    const val = (e.target as HTMLTextAreaElement).value;
                    smartRepliesSignal.value = replies.map((r, i) => {
                      if (String(i + 1) === reply.id || r.id === reply.id) {
                        return typeof r === 'string' ? val : { ...r, text: val };
                      }
                      return r;
                    });
                  }}
                  class="w-full p-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-100 focus:outline-none focus:border-cyan-500"
                  rows={3}
                />
              ) : (
                <p class="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">{reply.text}</p>
              )}
            </div>

            <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-800/60">
              <button
                onClick={() => setEditingId(editingId === reply.id ? null : reply.id)}
                class="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold rounded-lg cursor-pointer"
              >
                {editingId === reply.id ? 'Pronto' : 'Editar'}
              </button>
              <button
                onClick={() => handleSendReply(reply.text)}
                class="px-3 py-1 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-bold rounded-lg cursor-pointer shadow-md flex items-center gap-1"
              >
                <span>Enviar</span>
                <Send class="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Custom Draft Box */}
      <div class="pt-2 flex gap-2">
        <input
          type="text"
          placeholder="Escrever resposta personalizada..."
          value={customDraft}
          onInput={(e) => setCustomDraft((e.target as HTMLInputElement).value)}
          class="flex-1 px-3.5 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
        />
        <button
          onClick={() => {
            handleSendReply(customDraft);
            setCustomDraft('');
          }}
          class="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold cursor-pointer flex items-center gap-1.5"
        >
          <span>Enviar Customizado</span>
          <Send class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
